<template>
  <div class="non-decoded-payloads">
    <h1>{{ t('payloads.title') }}</h1>
    <el-input
      v-model="searchQuery"
      placeholder="Search by device"
      style="margin-bottom: 20px;"
    />
    <el-table :data="paginatedData" style="width: 100%">
      <el-table-column prop="device" label="Device" />
      <el-table-column prop="dt" label="Date" />
      <el-table-column prop="payload" label="Payload" />
      <el-table-column prop="payloadAsHex" label="Payload as Hex" />
    </el-table>
    <el-pagination
      @current-change="handleCurrentChange"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="filteredPayloads.length"
      layout="total, prev, pager, next, sizes"
      :page-sizes="[10, 20, 30, 40]"
      style="margin-top: 20px;"
    />

    <!-- Chart for daily quantities -->
    <div class="chart-container">
      <h2>{{ t('payloads.dailyQuantities') }}</h2>
      <LineChart :chart-data="dailyQuantities" chart-type="bar" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';
import LineChart from './LineChart.vue';

interface Payload {
  device: string;
  dt: string;
  payload: string;
  payloadAsHex: string;
  quantity: number;
}

const { t } = useI18n();
const searchQuery = ref('');
const payloads = ref<Payload[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const dailyQuantities = ref({
  labels: [] as string[],
  datasets: [] as Array<{
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
  }>
});

const fetchPayloads = async () => {
  try {
    const authStore = useAuthStore();
    const response = await axios.get<Payload[]>(`${import.meta.env.VITE_EXCEL_API_URL}/payloads`, {
      headers: {
        Authorization: `Bearer ${authStore.keycloak?.token}`
      }
    });
    payloads.value = response.data;
    calculateDailyQuantities();
  } catch (error) {
    console.error('Error fetching payloads:', error);
  }
};

const calculateDailyQuantities = () => {
  const quantities: Record<string, number> = {};
  
  payloads.value.forEach(item => {
    const date = new Date(item.dt).toLocaleDateString();
    quantities[date] = (quantities[date] || 0) + (item.quantity || 0);
  });

  dailyQuantities.value = {
    labels: Object.keys(quantities),
    datasets: [{
      label: t('payloads.quantity'),
      data: Object.values(quantities),
      backgroundColor: '#166b43'
    }]
  };
};

const filteredPayloads = computed(() => {
  return payloads.value.filter(item =>
    item.device.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredPayloads.value.slice(start, start + pageSize.value);
});

const handleCurrentChange = (page: number) => {
  currentPage.value = page;
};

onMounted(fetchPayloads);
</script>

<style scoped>
.non-decoded-payloads {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.chart-container {
  margin-top: 40px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}
</style> 